import { useEffect, useMemo, useState } from 'react';
import taskService from '../../../services/taskService';
import { userService } from '../../../services/userService';
import { toast } from 'react-toastify';
import useDebounce from '../../../hooks/useDebounce';
import { X, AlertTriangle } from 'lucide-react';

interface AssignTasksDrawerProps {
  open: boolean;
  onClose: () => void;
  preselectedEquipmentIds?: string[];
  preselectedTaskIds?: string[];
  onAssigned?: (result: { taskIds: string[]; count: number }) => void;
  mode?: 'assign' | 'reassign';
}

export default function AssignTasksDrawer({ 
  open, 
  onClose, 
  preselectedEquipmentIds, 
  preselectedTaskIds, 
  onAssigned,
  mode = 'assign'
}: AssignTasksDrawerProps) {
  const [engineersQuery, setEngineersQuery] = useState('');
  const [engineers, setEngineers] = useState<any[]>([]);
  const [selectedEngineerId, setSelectedEngineerId] = useState<string>('');
  const [equipmentIds, setEquipmentIds] = useState<string>('');
  const [taskIds, setTaskIds] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [assignmentReason, setAssignmentReason] = useState<string>('');
  const [selectedTasks, setSelectedTasks] = useState<any[]>([]);

  const debouncedQuery = useDebounce(engineersQuery, 300);

  useEffect(() => {
    if (!open) return;
    // Seed text inputs from preselected props
    setEquipmentIds((preselectedEquipmentIds || []).join(','));
    setTaskIds((preselectedTaskIds || []).join(','));
    
    // Fetch task details for preselected tasks
    if (preselectedTaskIds && preselectedTaskIds.length > 0) {
      fetchTaskDetails();
    }
    
    // Set default reason based on mode
    if (mode === 'reassign') {
      setAssignmentReason('Reassignment for optimal resource allocation');
    } else {
      setAssignmentReason('Initial task assignment');
    }
  }, [open, preselectedTaskIds, mode]);

  useEffect(() => {
    if (!open) return;
    void fetchEngineers();
  }, [debouncedQuery, open]);

  async function fetchTaskDetails() {
    if (!preselectedTaskIds || preselectedTaskIds.length === 0) return;
    
    try {
      const tasks = await Promise.all(
        preselectedTaskIds.map(async (taskId) => {
          try {
            // Note: You might need to implement a getTaskById method in taskService
            // For now, we'll use the existing getTasks method with task ID filter
            const response = await taskService.getTasks({ 
              page: 1, 
              limit: 1, 
              // taskId: taskId // This would need to be added to the backend API
            });
            return response.data?.docs?.[0] || null;
          } catch (error) {
            console.error(`Failed to fetch task ${taskId}:`, error);
            return null;
          }
        })
      );
      
      setSelectedTasks(tasks.filter(Boolean));
    } catch (error) {
      console.error('Failed to fetch task details:', error);
    }
  }

  async function fetchEngineers() {
    try {
      const { users } = await userService.getUsers({ name: debouncedQuery, status: 'active', role: 'site_engineer', limit: 10, page: 1 });
      setEngineers(users);
    } catch (e) {
      // swallow
    }
  }

  const normalizeIds = (arr?: string[]) => (Array.isArray(arr) ? arr.map((x)=>String(x)).filter((x)=>!!x && x !== 'null' && x !== 'undefined') : []);
  const eqIdsFromProps = normalizeIds(preselectedEquipmentIds);
  const taskIdsFromProps = normalizeIds(preselectedTaskIds);

  const effectiveEquipmentIds = useMemo(() => {
    if (eqIdsFromProps.length > 0) return eqIdsFromProps;
    return equipmentIds.split(',').map((x) => x.trim()).filter((x)=>!!x && x !== 'null' && x !== 'undefined');
  }, [eqIdsFromProps, equipmentIds]);

  const effectiveTaskIds = useMemo(() => {
    if (taskIdsFromProps.length > 0) return taskIdsFromProps;
    return taskIds.split(',').map((x) => x.trim()).filter((x)=>!!x && x !== 'null' && x !== 'undefined');
  }, [taskIdsFromProps, taskIds]);

  async function onAssign() {
    const eqIds = effectiveEquipmentIds;
    const tIds = effectiveTaskIds;
    if (!selectedEngineerId || (eqIds.length === 0 && tIds.length === 0)) {
      toast.error('Please select an engineer and tasks to assign');
      return;
    }
    
    if (mode === 'reassign' && !assignmentReason.trim()) {
      toast.error('Please provide a reason for reassignment');
      return;
    }
    
    setLoading(true);
    try {
      const payload: any = {
        'equipment-ids': eqIds.length ? eqIds : undefined,
        'task-ids': tIds.length ? tIds : undefined,
        'site-engineer-id': selectedEngineerId,
        reason: assignmentReason.trim() || undefined,
      };
      
      const result = await taskService.assignTasksToEngineer(payload);
      const actionText = mode === 'reassign' ? 'Reassigned' : 'Assigned';
      const assignmentData = result.data;
      toast.success(`${actionText} ${assignmentData.updatedCount} tasks to selected engineer`);
      
      if (onAssigned) onAssigned({ 
        taskIds: assignmentData.updatedTaskIds, 
        count: assignmentData.updatedCount 
      });
      onClose();
      setSelectedEngineerId('');
      setEquipmentIds('');
      setTaskIds('');
      setAssignmentReason('');
    } catch (e: any) {
      toast.error(e.message || `Failed to ${mode} tasks`);
    } finally {
      setLoading(false);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1000]">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-2xl bg-white shadow-xl flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-gray-900">
                {mode === 'reassign' ? 'Reassign Tasks' : 'Assign Tasks'}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {mode === 'reassign' ? 'Reassign selected tasks to new site engineers' : 'Assign selected tasks to site engineers'}
              </p>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Close"
            >
              <X size={20} className="text-gray-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6 space-y-6">
          {/* Selected Tasks Summary */}
          {selectedTasks.length > 0 && (
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-5 h-5 text-blue-600" />
                <h4 className="font-semibold text-blue-900">Selected Tasks ({selectedTasks.length})</h4>
              </div>
              
              {/* Group tasks by frequency for better display */}
              {(() => {
                const tasksByFrequency = selectedTasks.reduce((acc: any, task: any) => {
                  const freq = task.frequency || 'Other';
                  if (!acc[freq]) acc[freq] = [];
                  acc[freq].push(task);
                  return acc;
                }, {});
                
                return (
                  <div className="space-y-3 max-h-32 overflow-auto">
                    {Object.entries(tasksByFrequency).map(([frequency, tasks]: [string, any]) => (
                      <div key={frequency} className="border-l-2 border-blue-300 pl-3">
                        <div className="font-medium text-blue-800 text-xs mb-1">
                          📅 {frequency} Tasks ({tasks.length})
                        </div>
                        <div className="space-y-1">
                          {tasks.slice(0, 3).map((task: any) => (
                            <div key={task._id} className="flex items-center justify-between text-xs">
                              <span className="text-blue-900 truncate">{task.checkpointName || 'Unnamed Task'}</span>
                              {task.assignedEngineerId && mode === 'reassign' && (
                                <span className="text-blue-600 text-xs">Current: {task.assignedEngineerId.name}</span>
                              )}
                            </div>
                          ))}
                          {tasks.length > 3 && (
                            <div className="text-xs text-blue-600">...and {tasks.length - 3} more</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Engineer Search and Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search & Select Site Engineer</label>
            <input 
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
              value={engineersQuery} 
              onChange={(e) => setEngineersQuery(e.target.value)} 
              placeholder="Type name, email or mobile number" 
            />
            
            <div className="mt-3 max-h-48 overflow-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
              {engineers.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  {engineersQuery ? 'No engineers found matching your search' : 'Start typing to search for engineers'}
                </div>
              ) : (
                engineers.map((engineer) => {
                  const id = engineer._id || engineer.id;
                  const selected = selectedEngineerId === id;
                  return (
                    <label key={id} className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors ${selected ? 'bg-blue-50 border-l-4 border-blue-500' : ''}`}>
                      <input 
                        type="radio" 
                        name="selectedEngineer"
                        checked={selected} 
                        onChange={() => setSelectedEngineerId(id)}
                        className="border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className={`font-medium ${selected ? 'text-blue-900' : 'text-gray-900'}`}>{engineer.name}</div>
                        <div className={`text-sm ${selected ? 'text-blue-600' : 'text-gray-500'}`}>{engineer.email || engineer.mobile}</div>
                      </div>
                      <div className={`text-xs ${selected ? 'text-blue-500' : 'text-gray-400'}`}>
                        Site Engineer
                      </div>
                    </label>
                  );
                })
              )}
            </div>
            
            {selectedEngineerId && (
              <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                <div className="text-sm text-green-700">
                  ✓ Selected: {engineers.find(e => (e._id || e.id) === selectedEngineerId)?.name}
                </div>
              </div>
            )}
          </div>

          {/* Assignment Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Assignment Reason {mode === 'reassign' && <span className="text-red-500">*</span>}
            </label>
            <textarea
              value={assignmentReason}
              onChange={(e) => setAssignmentReason(e.target.value)}
              placeholder={mode === 'reassign' ? 'Please provide a reason for reassignment...' : 'Optional: Provide a reason for this assignment...'}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              rows={3}
            />
            <p className="text-xs text-gray-500 mt-1">
              {mode === 'reassign' ? 'Required field for audit trail' : 'This will be recorded in the assignment history'}
            </p>
          </div>

          {/* Assignment Summary */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <h4 className="font-semibold text-gray-900 mb-2">Assignment Summary</h4>
            <div className="text-sm text-gray-600 space-y-1">
              <div>• All selected tasks will be assigned to the selected engineer</div>
              <div>• Assignment will be tracked with full audit history</div>
              <div>• Engineer will receive notification of new assignments</div>
            </div>
          </div>

          {/* Manual Task/Equipment Input (fallback) */}
          {(effectiveEquipmentIds.length === 0 && effectiveTaskIds.length === 0) && (
            <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
                <h4 className="font-semibold text-yellow-900">Manual Input</h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-yellow-700 mb-1">Equipment IDs</label>
                  <input 
                    className="w-full px-3 py-2 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500" 
                    value={equipmentIds} 
                    onChange={(e) => setEquipmentIds(e.target.value)} 
                    placeholder="id1,id2,id3" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-yellow-700 mb-1">Task IDs</label>
                  <input 
                    className="w-full px-3 py-2 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500" 
                    value={taskIds} 
                    onChange={(e) => setTaskIds(e.target.value)} 
                    placeholder="id1,id2,id3" 
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {selectedEngineerId && (
                <span className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  Engineer selected: {engineers.find(e => (e._id || e.id) === selectedEngineerId)?.name}
                </span>
              )}
            </div>
            <div className="flex gap-3">
              <button 
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={onAssign}
                disabled={!selectedEngineerId || loading || (effectiveEquipmentIds.length === 0 && effectiveTaskIds.length === 0)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {loading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                {mode === 'reassign' ? 'Reassign Tasks' : 'Assign Tasks'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


